/**
 * Login, "wachtwoord vergeten" en "wachtwoord opnieuw instellen" — één scherm
 * met drie standen, net als de bundle.
 *
 * Endpoints, exact zoals daar:
 *   POST /api/admin/auth/login            {username, password}
 *   POST /api/admin/auth/forgot-password  {identifier}
 *   POST /api/admin/auth/reset-password   {token, new_password}
 *   GET  /api/admin/auth/instance         (agencynaam in de kop)
 *
 * De reset-stand wordt gekozen als er een ?token= (of #token=) in de URL
 * staat, of als het pad reset-password bevat — dezelfde regel als de bundle.
 */
import { useEffect, useState } from 'react'
import { haalMediaToken, setToken, USER_KEY } from '../lib/api'
import { markeerActiviteit, UITLOG_REDEN } from '../lib/inactiviteit'
import { instanceConfig } from '../lib/instance'

type Mode = 'login' | 'forgot' | 'reset'

export function Login({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [mode, setMode] = useState<Mode>('login')
  const merk = instanceConfig()
  const [instance, setInstance] = useState(merk.brand)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [identifier, setIdentifier] = useState('')
  const [token, setResetToken] = useState('')
  const [pw1, setPw1] = useState('')
  const [pw2, setPw2] = useState('')
  // 03-10-2026: na uitloggen wegens stilte (chatters) zegt het scherm waarom.
  const [err, setErr] = useState(() => {
    try {
      return sessionStorage.getItem(UITLOG_REDEN) === 'inactief'
        ? 'You were signed out after 1 hour of inactivity.' : ''
    } catch { return '' }
  })
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('token') || ''
      const h = (window.location.hash || '').match(/token=([^&]+)/)
      const t = q || (h ? decodeURIComponent(h[1]) : '')
      const isReset = window.location.pathname.includes('reset-password') ||
                      window.location.hash.includes('reset-password')
      if (t && (isReset || t.length > 20)) { setResetToken(t); setMode('reset') }
      else if (window.location.pathname.includes('forgot-password')) setMode('forgot')
    } catch { /* geen URL-parameters */ }
  }, [])

  useEffect(() => {
    fetch('/api/admin/auth/instance')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (typeof d?.instance_name === 'string' && d.instance_name.trim()) setInstance(d.instance_name.trim()) })
      .catch(() => {})
  }, [])

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault(); setErr(''); setBusy(true)
    try {
      const r = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // 03-10-2026: "Remember me" doet nu echt iets — met vinkje krijgen
        // eigenaar/admin/manager een refresh-cookie (30 dagen stil ingelogd).
        body: JSON.stringify({ username, password, remember }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { setErr(d.detail || 'Invalid credentials'); return }
      setToken(d.access_token)
      // 03-10-2026: inloggen is activiteit; anders telt een oude stand van gisteren.
      markeerActiviteit()
      try { sessionStorage.removeItem(UITLOG_REDEN) } catch { /* private mode */ }
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(d.user ?? { username }))
      } catch { /* private mode */ }
      // De mediasleutel meteen ophalen, zodat het eerste scherm al met de
      // sleutel werkt en niet eerst met het inlogtoken in de plaatjes-URL's.
      await haalMediaToken()
      onLoggedIn()
    } catch {
      setErr('Netwerkfout')
    } finally {
      setBusy(false)
    }
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault(); setNote(''); setBusy(true)
    try {
      await fetch('/api/admin/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim() }),
      })
      setNote('If that account exists, a reset link has been sent to the email on file. ' +
              'The link expires in 60 minutes. Check spam if it doesn’t arrive.')
    } finally {
      setBusy(false)
    }
  }

  async function submitReset(e: React.FormEvent) {
    e.preventDefault(); setErr('')
    if (pw1.length < 8) { setErr('Pick a password of at least 8 characters.'); return }
    if (pw1 !== pw2) { setErr('The two passwords don’t match.'); return }
    setBusy(true)
    try {
      const r = await fetch('/api/admin/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw1 }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { setErr(d.detail || 'Reset failed'); return }
      setErr('Password updated. Redirecting to sign-in…')
      setTimeout(() => { window.history.replaceState({}, '', '/'); setMode('login') }, 1500)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div className="card" style={{ width: 380, maxWidth: '94vw' }}>
        <div className="row" style={{ marginBottom: 14 }}>
          <div className="logo">{merk.initials}</div>
          <div>
            <b style={{ fontSize: 15 }}>{instance}</b>
            <div className="mut2" style={{ fontSize: 12 }}>{merk.subtitle}</div>
          </div>
        </div>

        {mode === 'login' ? (
          <form onSubmit={submitLogin}>
            <div className="field">
              <label>Username</label>
              <input className="inp" value={username} autoComplete="username"
                     onChange={(e) => setUsername(e.target.value)} />
            </div>
            <div className="field">
              <label>Password</label>
              <input className="inp" type="password" value={password} autoComplete="current-password"
                     onChange={(e) => setPassword(e.target.value)} />
            </div>
            <label className="chkrow">
              <span className={remember ? 'chk on' : 'chk'} onClick={() => setRemember((v) => !v)}>
                {remember ? '✓' : ''}
              </span>
              Remember me
            </label>
            {err ? <div style={{ color: 'var(--bad)', fontSize: 12, minHeight: 16 }}>{err}</div> : null}
            <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', marginTop: 8 }}>
              {busy ? 'Working…' : 'Sign in'}
            </button>
            <div className="row" style={{ justifyContent: 'center', marginTop: 10 }}>
              <a className="mut2" style={{ fontSize: 12, cursor: 'pointer' }}
                 onClick={() => { setMode('forgot'); setErr('') }}>
                Forgot password?
              </a>
            </div>
          </form>
        ) : null}

        {mode === 'forgot' ? (
          <form onSubmit={submitForgot}>
            <div className="mut2" style={{ fontSize: 12, marginBottom: 10 }}>
              Enter your username or email address; you will receive a reset link.
            </div>
            <div className="field">
              <label>Username or email</label>
              <input className="inp" value={identifier}
                     onChange={(e) => setIdentifier(e.target.value)} />
            </div>
            {note ? <div className="note" style={{ marginBottom: 8 }}>{note}</div> : null}
            <button className="btn primary" type="submit" disabled={busy || !identifier.trim()}
                    style={{ width: '100%' }}>
              {busy ? 'Working…' : 'Send reset link'}
            </button>
            <div className="row" style={{ justifyContent: 'center', marginTop: 10 }}>
              <a className="mut2" style={{ fontSize: 12, cursor: 'pointer' }}
                 onClick={() => setMode('login')}>Back to login</a>
            </div>
          </form>
        ) : null}

        {mode === 'reset' ? (
          <form onSubmit={submitReset}>
            <div className="mut2" style={{ fontSize: 12, marginBottom: 10 }}>
              Choose a new password of at least 8 characters.
            </div>
            <div className="field">
              <label>New password</label>
              <input className="inp" type="password" value={pw1}
                     onChange={(e) => setPw1(e.target.value)} />
            </div>
            <div className="field">
              <label>Repeat password</label>
              <input className="inp" type="password" value={pw2}
                     onChange={(e) => setPw2(e.target.value)} />
            </div>
            {err ? <div style={{ color: 'var(--bad)', fontSize: 12, minHeight: 16 }}>{err}</div> : null}
            <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%' }}>
              {busy ? 'Working…' : 'Save password'}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  )
}
